import { Logger, formatError } from '../../../../packages/logger/src';

/** Temporary Hydra-backed call queue client until the public calling SDK API is available. */
export type CallQueueAgentState = 'SIGN_IN' | 'SIGN_OUT' | 'AVAILABLE' | 'UNAVAILABLE' | 'WRAP_UP';

export type CallQueue = {
  id: string;
  name?: string;
  extension?: string;
  phoneNumber?: string;
  available?: boolean;
  allowLogOffEnabled?: boolean;
};

export type CallQueueSettings = {
  agentACDState: CallQueueAgentState | string;
  queues: CallQueue[];
};

type HydraResponse<T> = { body?: T } & T;
type QueueCatalogResponse = { queues?: CallQueue[] } | CallQueue[];

const QUEUE_SETTINGS_RESOURCE = 'telephony/config/people/me/settings/queues';
const QUEUE_CATALOG_RESOURCE = 'telephony/config/queues';
const AGENT_EVENTS = [
  'event:telephony_agent.sync',
  'event:telephony_agent.state',
  'event:telephony_agent.join',
];

const settingsResource = (lineOwnerId?: string): string => {
  if (!lineOwnerId) return QUEUE_SETTINGS_RESOURCE;
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(lineOwnerId)) {
    throw new Error('Invalid line owner ID for call queue request.');
  }
  return `${QUEUE_SETTINGS_RESOURCE.replace(/\/queues$/, '')}/secondarylines/${encodeURIComponent(lineOwnerId)}/queues`;
};

const responseBody = <T,>(response: HydraResponse<T> | T): T =>
  (response as HydraResponse<T>)?.body ?? (response as T);

const asQueueList = (response: QueueCatalogResponse): CallQueue[] => {
  if (Array.isArray(response)) return response;
  return response.queues || [];
};

/**
 * Reads the current user's call queue state from the Hydra service selected by
 * the initialized Webex SDK (integration in integration builds).
 */
export const getCallQueueSettings = async (lineOwnerId?: string): Promise<CallQueueSettings> => {
  const [settingsResponse, catalogResponse] = await Promise.all([
    window.webex.request({ method: 'GET', service: 'hydra', resource: settingsResource(lineOwnerId) }),
    window.webex.request({ method: 'GET', service: 'hydra', resource: QUEUE_CATALOG_RESOURCE }),
  ]);

  const settings = responseBody<{ agentACDState?: string; acdState?: string; queues?: CallQueue[] }>(settingsResponse);
  const queueNames = asQueueList(responseBody<QueueCatalogResponse>(catalogResponse))
    .reduce<Record<string, string>>((result, queue) => {
      if (queue.id && queue.name) result[queue.id] = queue.name;
      return result;
    }, {});

  const queues = (settings.queues || []).map((queue) => ({
    ...queue,
    name: queue.name || queueNames[queue.id],
  }));
  const agentACDState = settings.agentACDState || settings.acdState || 'SIGN_OUT';
  Logger.info(`WebexCallQueueService#getCallQueueSettings() :: ACD state ${agentACDState}, queue count ${queues.length}`);
  return { agentACDState, queues };
};

/** Updates agent state and/or queue membership. Hydra currently returns 204. */
export const updateCallQueueSettings = async (
  request: { agentACDState: CallQueueAgentState; queues: Pick<CallQueue, 'id' | 'available'>[] },
  lineOwnerId?: string
): Promise<void> => {
  try {
    await window.webex.request({
      method: 'PATCH',
      service: 'hydra',
      resource: settingsResource(lineOwnerId),
      body: {
        agentACDState: request.agentACDState,
        queues: request.queues.map(({ id, available }) => ({ id, available })),
      },
    });
  } catch (error) {
    Logger.error(`WebexCallQueueService#updateCallQueueSettings() :: ${formatError(error)}`);
    throw error;
  }
};

/** Subscribes to queue state changes and returns a cleanup function. */
export const subscribeToCallQueueUpdates = (onUpdate: () => void): (() => void) => {
  const mercury = window.webex?.internal?.mercury;
  if (!mercury?.on) return () => undefined;

  AGENT_EVENTS.forEach((eventName) => mercury.on(eventName, onUpdate));
  return () => {
    AGENT_EVENTS.forEach((eventName) => {
      if (typeof mercury.off === 'function') mercury.off(eventName, onUpdate);
      else if (typeof mercury.removeListener === 'function') mercury.removeListener(eventName, onUpdate);
    });
  };
};
